import { PublishStatus } from "api/config/chalice-types";
import { fetchPublishStatus } from "api/postsApi";
import { genericError } from "fixtures/globalConstants";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Post } from "store/posts/postConstants";
import { syncPostData } from "store/posts/postSlice";

interface UsePostRefreshProps {
  post: Post;
}

const usePostRefresh = ({ post }: UsePostRefreshProps) => {
  const dispatch = useDispatch();
  const [refreshPostTriesCount, setRefreshPostTriesCount] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshPost = useCallback(
    async (isManual = false) => {
      if (!post?.id || !post?.publish_results) {
        console.error(
          "Post ID or publish_results missing, cannot refresh status."
        );
        return;
      }

      setIsRefreshing(true);
      try {
        // Don't auto-refresh if all statuses are terminal
        if (
          Object.values(post.publish_results ?? {}).every((value) =>
            ["success", "failed"].includes((value as PublishStatus).state)
          )
        ) {
          // Still allow manual refresh
          if (!isManual) {
            setIsRefreshing(false);
            return;
          }
        }

        const updatedStatus = (await fetchPublishStatus(post.id)) as Record<
          string,
          PublishStatus
        >;
        dispatch(
          syncPostData({ post: { ...post, publish_results: updatedStatus } })
        );

        // Schedule next auto-refresh if needed
        if (
          !isManual &&
          Object.values(updatedStatus).some(
            (value) => !["success", "failed"].includes(value.state)
          )
        ) {
          const delay = Math.pow(1.5, refreshPostTriesCount) * 1000;
          setTimeout(() => {
            setRefreshPostTriesCount(refreshPostTriesCount + 1);
          }, delay);
        } else if (isManual) {
          // Reset retry count on manual refresh if successful and still pending items
          setRefreshPostTriesCount(0);
        }
      } catch (error) {
        genericError(error);
      }
      setIsRefreshing(false);
    },
    [post, dispatch, refreshPostTriesCount]
  );

  // Initial and scheduled auto-refresh effect
  useEffect(() => {
    // Only run effect if post details are present
    if (post?.id && post?.publish_results) {
      refreshPost();
    }
    // We only want this effect to run when the retry count changes,
    // not when the post object itself changes, to avoid infinite loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshPostTriesCount]);

  // Renamed manual trigger function for clarity
  const triggerManualRefresh = useCallback(() => {
    refreshPost(true);
  }, [refreshPost]);

  return { isRefreshing, triggerManualRefresh };
};

export default usePostRefresh;
